import {AxiosError, InternalAxiosRequestConfig} from "axios";
import {TokenStorage} from "./token";
import client from "api/client";
import {queries} from "shared/api/queries";

type RetryableRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
};

type AuthFailureHandler = () => void;

let requestInterceptorId: number | null = null;
let responseInterceptorId: number | null = null;
let authFailureHandler: AuthFailureHandler | null = null;

const clearAuthState = () => {
  TokenStorage.clear();
  authFailureHandler?.();
};

const setupRequestInterceptor = () => {
  if (requestInterceptorId !== null) {
    return;
  }
  
  requestInterceptorId = client.interceptors.request.use(async (config) => {
    const accessToken = TokenStorage.getToken();

    if (accessToken) {
      config.headers["authorization"] = `Bearer ${accessToken}`;
    }
    
    return config;
  }, (error) => Promise.reject(error));
};

setupRequestInterceptor();

export const setResponse = (onAuthFailure: AuthFailureHandler) => {
  authFailureHandler = onAuthFailure;
  
  if (responseInterceptorId !== null) {
    return;
  }
  
  responseInterceptorId = client.interceptors.response.use((response) => response, (error: AxiosError) => {
    const status = error.response?.status;
    const config = error.config as RetryableRequestConfig | undefined;
    const url = config?.url ?? "";
    
    if (status === 403) {
      clearAuthState();
      return Promise.reject(error);
    }
    
    if (status !== 401) {
      return Promise.reject(error);
    }
    
    if (!config || config._retry) {
      clearAuthState();
      return Promise.reject(error);
    }
    
    if (url.endsWith(queries.auth.token.refresh)) {
      clearAuthState();
      return Promise.reject(error);
    }
    
    config._retry = true;
    
    return TokenStorage.cachedGetNewToken()
                       .then((response) => {
                         if (url.includes("/auth/")) {
                           return response;
                         }
                         
                         return client.request(config);
                       })
                       .catch((refreshError) => {
                         clearAuthState();
                         return Promise.reject(refreshError);
                       });
  });
};
